import React, { useCallback, useMemo, useState } from 'react';
import {
  FieldValues,
  Path,
  useController,
  useFormContext,
} from 'react-hook-form';
import FieldError from './FieldError';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { phoneNumberPattern } from 'utilities/variables';
import { useGetPage } from 'hooks/useGetPage';
import { pageIds } from 'utilities/constants';
import InputComponent, {
  SelectWithFlagsValues,
} from 'components/inputComponent';
import {
  normalizePhoneNumber,
  parseMappedCountriesIntoSelectValues,
} from 'utilities/functions';
import { useGetCountriesQuery } from 'graphql/generated/hasura';
import FieldLabel from './FieldLabel';
import { PhoneDataProps } from '../../dynamicQuestionnaire/interfaces/SignUpProviderModule.interface';
import { twMerge } from 'tailwind-merge';

interface MfaPhoneInputComponentProps {
  country: string;
  name: string;
  label: string;
  isRequired?: boolean;
  disabled?: boolean;
  withoutExt?: boolean;
  extension?: string;
  onCountryChange: (country: SelectWithFlagsValues) => void;
  onPhoneNumberChange: (phoneNumber: string) => void;
  onExtensionChange: (phoneNumber: string) => void;
  phoneNumber: string;
  currentPhoneNumber?: string;
  phoneError?: string;
}

interface ControlledMfaInputProps<T extends FieldValues> {
  name: Path<T>;
  label: string;
  defaultValue?: string | null;
  isRequired?: boolean;
  disabled?: boolean;
  withoutExt?: boolean;
}

export function mergeFlagSVGs(flagA: string, flagB: string): string {
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 560 740" width="30" height="30">

    <svg x="0" y="0" width="480" height="320" viewBox="0 0 513 342">
      ${stripSVGWrapper(flagA)}
    </svg>

    <svg x="0" y="342" width="480" height="320" viewBox="0 0 513 342">
      ${stripSVGWrapper(flagB)}
    </svg>
  </svg>`;
}

function stripSVGWrapper(svg: string): string {
  return svg
    .replace(/^<svg[^>]*>/, '')
    .replace(/<\/svg>\s*$/, '')
    .trim();
}

/** Merges labels */
export function mergeLabels(labels: string[]): string {
  return labels.join(' / ');
}

export const MfaPhoneInputComponent: React.FC<MfaPhoneInputComponentProps> = ({
  onCountryChange,
  onPhoneNumberChange,
  onExtensionChange,
  country,
  name,
  label,
  isRequired,
  disabled,
  phoneNumber,
  extension,
  phoneError,
  withoutExt,
}) => {
  const { data: locale, loading } = useGetPage({
    locale: 'en',
    pageId: pageIds.MULTI_FACTOR_AUTH,
  });

  const { data: countriesData } = useGetCountriesQuery({});

  const countries = useMemo(() => {
    if (!countriesData?.countries) return [];

    const sorted = [...countriesData.countries].sort(
      (a, b) => parseInt(b.code) - parseInt(a.code),
    );

    // Group by code
    const grouped = sorted.reduce<
      Record<string, { code: string; label: string; flag: string }[]>
    >((acc, country) => {
      const key = country.code;
      if (!acc[key]) acc[key] = [];
      acc[key].push({
        code: country.code as string,
        label: country.name,
        flag: country.flag || '',
      });
      return acc;
    }, {});

    // Merge duplicates
    return Object.values(grouped).map((group) => {
      if (group.length === 1) return group[0];

      const [flagA, flagB] = group.map((item) => item.flag).filter(Boolean);

      return {
        code: group[0].code,
        label: mergeLabels(group.map((c) => c.label)),
        flag: flagA && flagB ? mergeFlagSVGs(flagA, flagB) : flagA || flagB,
      };
    });
  }, [countriesData?.countries]);

  const parseSelectedValue = (
    currentCountry: string,
  ): SelectWithFlagsValues | undefined => {
    const values = parseMappedCountriesIntoSelectValues(countries);
    const matchingValue = values.find((item) => item.value === currentCountry);

    if (
      matchingValue &&
      matchingValue.label &&
      matchingValue.value &&
      matchingValue.flag
    ) {
      return {
        label: matchingValue.label,
        value: matchingValue.value,
        flag: matchingValue.flag,
      };
    } else {
      return undefined;
    }
  };

  const getMemoizedDefaultParsedValue = useMemo(
    () => parseMappedCountriesIntoSelectValues(countries)[0],
    [countries],
  );

  const getMemoizedParsedCountries = useMemo(
    () => parseMappedCountriesIntoSelectValues(countries),
    [countries],
  );

  if (loading && !locale) return null;

  return (
    <div className="flex flex-col desktop:flex-row gap-2.5">
      <div className="flex flex-col items-left w-full">
        <FieldLabel name={name} label={label} isRequired={isRequired} />

        <div className="flex flex-col w-full desktop:flex-row justify-between gap-2.5">
          <div className="flex flex-col w-full">
            <div className="flex flex-row w-full desktop:flex-grow">
              <InputComponent
                testID="phoneNumber-country-select"
                type="select-with-country-icons"
                autoComplete="none"
                name="country"
                isDisabled={disabled}
                borderRadiusRight={true}
                errorStatus={!!phoneError}
                selectInputWithFlagsProps={{
                  currentValue: parseSelectedValue(country),
                  defaultValue:
                    getMemoizedDefaultParsedValue as SelectWithFlagsValues,
                  options:
                    getMemoizedParsedCountries as SelectWithFlagsValues[],
                  onSelectChange: (e) =>
                    onCountryChange(e as SelectWithFlagsValues),
                }}
              />

              <InputComponent
                testID="phoneNumber-input"
                disableTelWidth={true}
                isDisabled={disabled}
                disableAutocomplete={true}
                type="text"
                pattern={phoneNumberPattern}
                name={name}
                errorStatus={!!phoneError}
                onChange={(e) => {
                  return onPhoneNumberChange(e.target.value);
                }}
                borderRadiusLeft={true}
                value={phoneNumber}
                placeholder={locale?.mfaSMS.phoneInputPlaceholder}
                customInputClassname="h-[46px] !w-full !text-btn-black !font-medium"
                maxLengthValue={12}
                noMarginBottom
              />
            </div>
          </div>
        </div>
      </div>
      <div
        className={twMerge('flex flex-col items-left', withoutExt && 'hidden')}
      >
        <FieldLabel
          name={'phoneNumberExtension'}
          label={locale?.extensionLabel ?? 'Ext.'}
        />

        <div className="flex flex-grow w-full desktop:w-[100px] max-h-[58px]">
          <InputComponent
            value={extension}
            flexGrow={1}
            isDisabled={disabled}
            type="ext"
            noMarginBottom
            autoComplete="none"
            name="phoneNumberExtension"
            onChange={(e) => onExtensionChange(e.target.value)}
            maxLengthValue={4}
            customInputClassname="h-[48px] text-neutral-800"
            placeholder={locale?.extentionPlaceholder ?? 'Enter ext'}
          />
        </div>
      </div>
    </div>
  );
};

const ControlledPhoneNumberInput = <T extends FieldValues>({
  name,
  label,
  disabled,
  isRequired,
  defaultValue = '1',
  withoutExt = false,
}: ControlledMfaInputProps<T>) => {
  const { control } = useFormContext<T>();

  const {
    field,
    fieldState: { error },
  } = useController({
    name,
    control,
    defaultValue: defaultValue as never,
  });

  const parsed = useMemo(() => {
    const val = field.value?.startsWith('+') ? field.value : `+${field.value}`;
    return parsePhoneNumberFromString(val);
  }, [field.value]);

  const [country, setCountry] = useState(
    () => parsed?.countryCallingCode ?? '1',
  );
  const [number, setNumber] = useState(() => parsed?.nationalNumber ?? '');
  const [extension, setExtension] = useState(parsed?.ext ?? '');

  const getFullNumber = useCallback(
    ({ country, phone: number, extension }: PhoneDataProps) => {
      return `+${country}${number}${extension ? `ext.${extension}` : ''}`;
    },
    [],
  );

  const handleCountryChange = (selectedCountry: SelectWithFlagsValues) => {
    const newCountry = selectedCountry.value;
    setCountry(newCountry);
    const fullNumber = getFullNumber({
      country: newCountry,
      phone: number,
      extension,
    });
    field.onChange(fullNumber);
  };

  const handlePhoneNumberChange = (newNumberValue: string) => {
    const sanitizedNumber = normalizePhoneNumber(newNumberValue);
    setNumber(sanitizedNumber);
    const fullNumber = getFullNumber({
      country,
      phone: sanitizedNumber,
      extension,
    });

    field.onChange(fullNumber);
  };

  const handleExtensionChange = (newExtensionValue: string) => {
    const sanitizedExt = normalizePhoneNumber(newExtensionValue);
    setExtension(sanitizedExt);
    const fullNumber = getFullNumber({
      country,
      phone: number,
      extension: sanitizedExt,
    });
    field.onChange(fullNumber);
  };

  return (
    <div id={name} className="w-full">
      <MfaPhoneInputComponent
        name={name}
        label={label}
        disabled={disabled}
        isRequired={isRequired}
        country={country}
        phoneNumber={number}
        extension={extension}
        withoutExt={withoutExt}
        onCountryChange={handleCountryChange}
        onPhoneNumberChange={handlePhoneNumberChange}
        onExtensionChange={handleExtensionChange}
        phoneError={error?.message}
      />
      {error && <FieldError>{error.message}</FieldError>}
    </div>
  );
};

export default ControlledPhoneNumberInput;
